import { useState } from 'react';
import { useDebouncedCallback } from 'use-debounce';
import { useSearchTracksPartnersQuery } from 'services/tracks/tracks';
import { AutocompleteWithEdit } from 'components/common/AutocompleteWithEdit/AutocompleteWithEdit';
import { ResolverOption } from '../reducer';

/* Marks an option that exists only as another row's pending "create new library" —
   nothing is created server-side until FINISH IMPORT, so these names can't come from the
   partners search. QwireTracksFields routes a pick of one to onCreate, not onChange. */
export const PENDING_LIBRARY_OPTION_ID = 'new';

type LibrarySelectProps = {
  value: ResolverOption | null;
  placeholder: string;
  onChange: (option: ResolverOption | null) => void;
  onCreate: (name: string) => void;
  onClear: () => void;
  pendingNewLibraries?: string[];
  disabled?: boolean;
};

const LibrarySelect = ({
  value,
  placeholder,
  onChange,
  onCreate,
  onClear,
  pendingNewLibraries = [],
  disabled,
}: LibrarySelectProps) => {
  const [term, setTerm] = useState('');
  const { data, isFetching } = useSearchTracksPartnersQuery({ term, limit: 0 });

  const debouncedSetTerm = useDebouncedCallback((_, inputValue) => {
    setTerm(inputValue ?? '');
  }, 300);

  const fetched = [...(data?.content ?? [])].sort((a, b) => a.name.localeCompare(b.name));
  const pending = pendingNewLibraries
    .filter((name) => !fetched.some((option) => option.name.toLowerCase() === name.toLowerCase()))
    .map((name) => ({ id: PENDING_LIBRARY_OPTION_ID, name }));
  const options = [...pending, ...fetched];

  return (
    <AutocompleteWithEdit
      options={options}
      value={value}
      onChange={onChange}
      onCreate={onCreate}
      onClear={onClear}
      onInputChange={debouncedSetTerm}
      loading={isFetching}
      size="small"
      disabled={disabled}
      inputProps={{ placeholder }}
    />
  );
};

export default LibrarySelect;
